import { useEffect, useState } from 'react';
import { vendorApi } from '../../api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Palette, Globe, Save, Loader2, Copy, ExternalLink, ImagePlus, MapPin, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const APP_ORIGIN = window.location.origin;

export default function VendorSettings() {
  const { user, login } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ app_name: '', login_slug: '', primary_color: '#3b82f6', logo_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    vendorApi.getMe()
      .then(({ data }) => {
        if (cancelled) return;
        setMe(data);
        setForm({
          app_name: data.app_name || '',
          login_slug: data.login_slug || '',
          primary_color: data.primary_color || '#3b82f6',
          logo_url: data.logo_url || '',
        });
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await vendorApi.updateMe({
        app_name: form.app_name.trim() || 'CredBloom',
        login_slug: form.login_slug.trim() || undefined,
        primary_color: form.primary_color,
        logo_url: form.logo_url.trim() || null,
      });
      setMe(data);
      toast.success('Settings saved — refresh to see the new branding everywhere');
      // Mutate the stored user so the sidebar logo updates immediately
      try {
        const cached = JSON.parse(localStorage.getItem('handleey_user') || '{}');
        if (cached) {
          cached.vendor = { ...(cached.vendor || {}), ...data };
          localStorage.setItem('handleey_user', JSON.stringify(cached));
          if (login) login(cached, localStorage.getItem('handleey_token'));
        }
      } catch {/* non-fatal */}
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-muted-foreground" /></div>
  );

  const loginUrl = `${APP_ORIGIN}/vendor/${form.login_slug}/login`;

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto space-y-6" data-testid="vendor-settings-page">
      <header>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Your brand and client-facing login URL.</p>
      </header>

      <Card>
        <CardContent className="p-6 space-y-5">
          <h2 className="text-base font-semibold flex items-center gap-2"><Palette size={16} /> White-Label Branding</h2>

          <div className="space-y-1.5">
            <Label className="text-xs">App Name</Label>
            <Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="Acme Reviews" data-testid="vendor-app-name-input" />
            <p className="text-[11px] text-muted-foreground">Shown in the sidebar, browser tab, and customer-facing review/event pages of your clients.</p>
          </div>

          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">Login Slug</Label>
              <Input value={form.login_slug} onChange={(e) => setForm({ ...form, login_slug: e.target.value.toLowerCase() })} data-testid="vendor-slug-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Primary Color</Label>
              <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-9 w-16 p-1" data-testid="vendor-color-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1"><ImagePlus size={11} /> Logo URL <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input type="url" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://your-cdn.com/logo.png" />
            <p className="text-[11px] text-muted-foreground">Square logo recommended (≥ 64×64 px). Replaces the app-name first letter in the sidebar.</p>
          </div>

          <div className="p-3 rounded-lg bg-muted/40 border border-border space-y-2">
            <p className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground"><Globe size={11} /> Your client login URL</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono px-2.5 py-1.5 rounded bg-background border border-border truncate" data-testid="vendor-login-url">
                {loginUrl}
              </code>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => { navigator.clipboard.writeText(loginUrl); toast.success('URL copied'); }} title="Copy">
                <Copy size={13} />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => window.open(loginUrl, '_blank', 'noopener,noreferrer')} title="Open">
                <ExternalLink size={13} />
              </Button>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="vendor-save-settings-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-2 text-sm">
          <h2 className="text-base font-semibold">Your Vendor Key</h2>
          <p className="text-xs text-muted-foreground">Use this to sign in to your own admin panel.</p>
          <code className="block text-base font-mono px-3 py-2 rounded bg-muted text-foreground">
            {me?.vendor_key || user?.vendor?.vendor_key || '—'}
          </code>
        </CardContent>
      </Card>

      <VendorPlacesKeyCard />
    </div>
  );
}

function VendorPlacesKeyCard() {
  const [me, setMe] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    vendorApi.getMe()
      .then(({ data }) => { if (!cancelled) setMe(data); })
      .catch(() => null)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await vendorApi.updateMe({ google_places_api_key: newKey.trim() });
      const { data } = await vendorApi.getMe();
      setMe(data); setNewKey('');
      toast.success(newKey.trim() ? 'Places API key saved — all your clients now inherit it' : 'Places API key cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardContent className="p-6 space-y-3">
        <h2 className="text-base font-semibold flex items-center gap-2"><MapPin size={16} /> Google Places API Key</h2>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Set this once and every client you create will inherit it — no per-client Places API prompt. Replaces the
          CredBloom-wide default for your clients only.
        </p>
        {me?.google_places_api_key_set ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={14} /> Configured ({me.google_places_api_key_masked})
          </div>
        ) : (
          <div className="text-sm text-amber-600 dark:text-amber-400">
            No vendor-specific key yet — clients will fall back to CredBloom's default key (if any).
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">{me?.google_places_api_key_set ? 'Replace with new key' : 'New API Key'}</Label>
          <div className="relative">
            <Input
              type={showKey ? 'text' : 'password'}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="AIza…"
              className="pr-9 font-mono text-xs"
              data-testid="vendor-places-input"
            />
            <button type="button" onClick={() => setShowKey(s => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          {me?.google_places_api_key_set && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!window.confirm('Clear the vendor Places API key? Your clients will fall back to CredBloom defaults.')) return;
                setSaving(true);
                try {
                  await vendorApi.updateMe({ google_places_api_key: '' });
                  const { data } = await vendorApi.getMe();
                  setMe(data); setNewKey('');
                  toast.success('Key cleared');
                } finally { setSaving(false); }
              }}
              className="text-destructive"
              data-testid="vendor-places-clear-btn"
            >
              Clear
            </Button>
          )}
          <Button onClick={save} disabled={saving || !newKey.trim()} className="gap-2 ml-auto" data-testid="vendor-places-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
